import type { ReportListItem } from './reportsClient'



const WPS_TAG = 'WPS'



/**

 * Output of `planWpsDefaults`. Drives the seed values of the WPS No. /

 * Rev. inputs on the WPS PQR picker, plus optional hints.

 */

export interface WpsPlannerResult {

  defaultWpsNumber: string

  defaultRevision: string

  lastGeneratedHint: {

    wpsNumber: string

    revision: string

    uploadedAt: string

    generatedByEmail: string | null

  } | null

}



export interface WpsPlannerInput {

  reports: ReportListItem[]

  /**

   * Union of weld ids from the selected PQR train(s). The WPS train

   * identity is the sorted weld-set key (same as PQR multi-weld trains).

   */

  weldIds: string[]

}



function trainKey(weldIds: string[] | null | undefined): string {

  if (!weldIds || weldIds.length === 0) return ''

  return [...weldIds].map((s) => s.trim()).filter(Boolean).sort().join('|')

}



function reportWeldSet(r: ReportListItem): string[] {

  if (Array.isArray(r.weldIds) && r.weldIds.length > 0) {

    return r.weldIds.filter((s): s is string => typeof s === 'string' && s.length > 0)

  }

  if (typeof r.weldId === 'string' && r.weldId.length > 0) return [r.weldId]

  return []

}



function revAsNumber(rev: string | undefined): number {

  if (typeof rev !== 'string') return 0

  const trimmed = rev.trim()

  if (!/^\d+$/.test(trimmed)) return 0

  return parseInt(trimmed, 10)

}



function isLive(report: ReportListItem): boolean {

  return !(typeof report.deletedAt === 'string' && report.deletedAt.trim().length > 0)

}



function isWpsReport(r: ReportListItem): boolean {

  return r.tag === WPS_TAG

}



/**

 * Compute default WPS No. / Rev. for a weld-scope union derived from

 * selected PQR train(s). Matches prior WPS rows by stored `weldIds`

 * train key; legacy `bevelId`-only rows are ignored for matching.

 */

export function planWpsDefaults(input: WpsPlannerInput): WpsPlannerResult {

  const liveReports = input.reports.filter(isLive).filter(isWpsReport)

  const inputTrain = trainKey(input.weldIds)



  const forThisTrain = inputTrain

    ? liveReports.filter((r) => trainKey(reportWeldSet(r)) === inputTrain)

    : []



  if (forThisTrain.length === 0) {

    const uniqueWpsNos = new Set<string>()

    for (const r of liveReports) {

      if (typeof r.wpsNumber === 'string' && r.wpsNumber.length > 0) {

        uniqueWpsNos.add(r.wpsNumber)

      }

    }

    const nextIndex = uniqueWpsNos.size + 1

    return {

      defaultWpsNumber: `WPS-${String(nextIndex).padStart(3, '0')}`,

      defaultRevision: '0',

      lastGeneratedHint: null

    }

  }



  const sortedByTime = [...forThisTrain].sort((a, b) =>

    (b.uploadedAt || '').localeCompare(a.uploadedAt || '')

  )

  const latest = sortedByTime[0]

  const proposedWpsNumber =

    typeof latest.wpsNumber === 'string' && latest.wpsNumber.length > 0

      ? latest.wpsNumber

      : 'WPS-001'



  let maxRev = -1

  for (const r of forThisTrain) {

    if (r.wpsNumber === proposedWpsNumber) {

      const n = revAsNumber(r.revision)

      if (n > maxRev) maxRev = n

    }

  }

  const defaultRevision = String(Math.max(maxRev + 1, 0))



  const generatedByEmail =

    latest.generatedBy &&

    typeof latest.generatedBy.email === 'string' &&

    latest.generatedBy.email.length > 0

      ? latest.generatedBy.email

      : null



  return {

    defaultWpsNumber: proposedWpsNumber,

    defaultRevision,

    lastGeneratedHint: {

      wpsNumber: proposedWpsNumber,

      revision: typeof latest.revision === 'string' ? latest.revision : '0',

      uploadedAt: latest.uploadedAt || '',

      generatedByEmail

    }

  }

}

